import { auth, googleProvider } from "./config";
import {
    signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, getRedirectResult,
    signOut, sendPasswordResetEmail, updatePassword as firebaseUpdatePassword, onAuthStateChanged,
    EmailAuthProvider, reauthenticateWithCredential, multiFactor, RecaptchaVerifier, createUserWithEmailAndPassword,
    updateProfile, PhoneAuthProvider, PhoneMultiFactorGenerator, TotpMultiFactorGenerator, getMultiFactorResolver
} from "firebase/auth";


export const loginWithGoogle = async () => {
    if (!auth) throw new Error("Firebase Auth not initialized");
    try {
        return await signInWithPopup(auth, googleProvider);
    } catch (error) {
        if (error.code === 'auth/popup-blocked') {
            // Fallback to redirect when popup is blocked
            return signInWithRedirect(auth, googleProvider);
        }
        throw error;
    }
};

export const getGoogleRedirectResult = async () => {
    if (!auth) return null;
    return getRedirectResult(auth);
};

export const registerWithEmail = async (email, password, username) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (username) {
        await updateProfile(userCredential.user, { displayName: username });
        await userCredential.user.reload(); // wait for profile update to settle
    }
    return auth.currentUser; // fresh reference instead of the stale local one
};

export const loginWithEmail = async (email, password) => {
    if (!auth) throw new Error("Firebase Auth not initialized");
    return signInWithEmailAndPassword(auth, email, password);
};

export const logout = async () => {
    if (!auth) throw new Error("Firebase Auth not initialized");
    return signOut(auth);
};

export const resetPassword = async (email) => {
    if (!auth) throw new Error("Firebase Auth not initialized");
    return sendPasswordResetEmail(auth, email);
};

export const updateUserPassword = async (newPassword) => {
    if (!auth) throw new Error("Firebase Auth not initialized");
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");
    return firebaseUpdatePassword(user, newPassword);
};

// Change password with reauthentication (requires current password for security)
export const changePasswordWithReauth = async (currentPassword, newPassword) => {
    if (!auth) throw new Error("Firebase Auth not initialized");
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently signed in");
    if (!user.email) throw new Error("User email not found");

    // Re-authenticate the user with their current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Now update to the new password
    return firebaseUpdatePassword(user, newPassword);
};

export const subscribeToAuthChanges = (callback) => {
    if (!auth) {
        callback(null);
        return () => { };
    }
    return onAuthStateChanged(auth, callback);
};


// ============================================
// MFA (Multi-Factor Authentication) Functions
// ============================================

// Get the auth instance for MFA operations
export const getAuthInstance = () => auth;

// Check if user has MFA enabled
export const isMfaEnabled = () => {
    if (!auth?.currentUser) return false;
    const enrolledFactors = multiFactor(auth.currentUser).enrolledFactors;
    return enrolledFactors.length > 0;
};

// Get enrolled MFA factors
export const getEnrolledMfaFactors = () => {
    if (!auth?.currentUser) return [];
    return multiFactor(auth.currentUser).enrolledFactors;
};

// Initialize RecaptchaVerifier for phone MFA
export const initRecaptchaVerifier = (containerId) => {
    if (!auth) throw new Error("Firebase Auth not initialized");
    return new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
        callback: () => {
            console.log('reCAPTCHA solved');
        }
    });
};

// Start phone MFA enrollment - Step 1: Send verification code
export const startPhoneMfaEnrollment = async (phoneNumber, recaptchaVerifier) => {
    if (!auth?.currentUser) throw new Error("No user is currently signed in");

    const session = await multiFactor(auth.currentUser).getSession();
    const phoneInfoOptions = {
        phoneNumber: phoneNumber,
        session: session
    };

    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);

    return verificationId;
};

// Complete phone MFA enrollment - Step 2: Verify code and enroll
export const completePhoneMfaEnrollment = async (verificationId, verificationCode, displayName = 'Phone') => {
    if (!auth?.currentUser) throw new Error("No user is currently signed in");

    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

    await multiFactor(auth.currentUser).enroll(multiFactorAssertion, displayName);
    return true;
};

// Start TOTP MFA enrollment (Authenticator App)
export const startTotpMfaEnrollment = async () => {
    if (!auth?.currentUser) throw new Error("No user is currently signed in");

    const session = await multiFactor(auth.currentUser).getSession();
    const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);

    // Generate QR code URL for authenticator apps
    const qrCodeUrl = totpSecret.generateQrCodeUrl(
        auth.currentUser.email,
        'HireHuddle'
    );

    return {
        secret: totpSecret,
        secretKey: totpSecret.secretKey,
        qrCodeUrl: qrCodeUrl
    };
};

// Complete TOTP MFA enrollment
export const completeTotpMfaEnrollment = async (totpSecret, verificationCode, displayName = 'Authenticator App') => {
    if (!auth?.currentUser) throw new Error("No user is currently signed in");

    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode
    );

    await multiFactor(auth.currentUser).enroll(multiFactorAssertion, displayName);
    return true;
};

// Unenroll MFA factor
export const unenrollMfaFactor = async (factorUid) => {
    if (!auth?.currentUser) throw new Error("No user is currently signed in");

    const enrolledFactors = multiFactor(auth.currentUser).enrolledFactors;
    const factorToRemove = enrolledFactors.find(f => f.uid === factorUid);

    if (!factorToRemove) throw new Error("MFA factor not found");

    await multiFactor(auth.currentUser).unenroll(factorToRemove);
    return true;
};

// Handle MFA challenge during sign-in
export const resolveMfaSignIn = async (error, verificationCode, selectedFactorIndex = 0) => {
    const resolver = getMultiFactorResolver(auth, error);
    const selectedHint = resolver.hints[selectedFactorIndex];

    const phoneInfoOptions = {
        multiFactorHint: selectedHint,
        session: resolver.session
    };

    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, RecaptchaVerifier);

    return { verificationId, resolver };
};

// Complete phone MFA verification during sign-in
export const completePhoneMfaSignIn = async (verificationId, verificationCode, resolver) => {
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

    const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
    return userCredential.user;
};

// Ensure authenticated for hiring managers (requires real Firebase Auth login)
export const ensureAnonymousAuth = async () => {
    if (!auth) {
        console.error("Firebase Auth not initialized");
        return null;
    }

    // If already signed in with Firebase Auth, return current user
    if (auth.currentUser) {
        console.log("Firebase Auth: Already signed in as", auth.currentUser.email);
        return auth.currentUser;
    }

    // No current user - hiring manager needs to log in again
    console.warn("Firebase Auth: No authenticated user. Hiring manager must log in.");
    return null;
};

// Helper to check if user is authenticated with Firebase
export const isFirebaseAuthenticated = () => {
    return auth?.currentUser !== null;
};

// Get current Firebase auth user
export const getCurrentFirebaseUser = () => {
    return auth?.currentUser || null;
};